import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Mail, Lock, User as UserIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";

import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";

export default function Auth() {
  useSEO({
    title: "Sign In / Create Account — InsureGenie India",
    description: "Sign in to InsureGenie to save your insurance chats, compare policies and revisit AI-powered guidance across devices.",
  });
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "register" ? "register" : "login";
  const pendingPrompt = params.get("prompt");
  const oauthError = params.get("error");
  const oauthDetail = params.get("detail");
  const [tab, setTab] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, login, register } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");

  useEffect(() => {
    if (user) {
      const dest = pendingPrompt ? `/chat?prompt=${encodeURIComponent(pendingPrompt)}` : "/chat";
      navigate(dest, { replace: true });
    }
  }, [user, navigate, pendingPrompt]);

  // Show a visible error toast when we land here from a failed OAuth callback
  // (previously the failure was silent — user saw "Welcome back" as if nothing happened).
  // NOTE: use window.history.replaceState to clean the URL, NOT navigate() — the
  // React-Router transition causes an immediate re-render that prevents Sonner
  // from mounting its Toaster in time to render the queued toast.
  // NOTE: defer toast.error via queueMicrotask so it runs AFTER the parent
  // <Toaster/> subscribes to sonner's store (React runs child useEffects before
  // parent useEffects — without this defer the toast is dropped).
  const errorHandledRef = useRef(false);
  useEffect(() => {
    if (errorHandledRef.current) return;
    if (oauthError !== "google") return;
    errorHandledRef.current = true;
    const msg = oauthDetail
      ? `Google sign-in failed: ${oauthDetail}`
      : "Google sign-in did not complete. Please try again, or use email sign-in below.";
    queueMicrotask(() => {
      toast.error(msg, { duration: 8000, id: "google-oauth-error" });
    });
    // Clean the URL without triggering a React Router transition.
    try {
      window.history.replaceState({}, "", "/auth");
    } catch (_e) { /* non-fatal */ }
  }, [oauthError, oauthDetail]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back to InsureGenie.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Account created. Let's chat.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/chat";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="auth-page" className="min-h-screen bg-background text-foreground flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex relative w-1/2 bg-primary text-primary-foreground p-12 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-10" />
        <div
          className="absolute inset-0 opacity-20 mix-blend-overlay"
          style={{
            backgroundImage: "url('https://images.pexels.com/photos/34337252/pexels-photo-34337252.jpeg')",
            backgroundSize: "cover", backgroundPosition: "center",
          }}
        />
        <Logo className="relative !text-white [&_span:first-child]:!bg-white [&_span:first-child]:!text-primary [&_.font-display]:!text-white" />
        <div className="relative max-w-md">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent mb-3">InsureGenie</p>
          <h2 className="font-display text-4xl font-semibold leading-tight tracking-tight text-balance">
            Insurance, finally explained like a conversation.
          </h2>
          <p className="mt-4 text-white/70 leading-relaxed">
            Sign in to save your chats, revisit comparisons, and pick up where you left off — across devices.
          </p>
        </div>
        <div className="relative text-[12px] text-white/60">
          © {new Date().getFullYear()} InsureGenie · Educational guidance only.
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border/60">
          <div className="lg:hidden"><Logo /></div>
          <span className="hidden lg:block" />
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link to="/"><Button data-testid="auth-back-home-btn" variant="ghost" size="sm" className="rounded-full">← Back home</Button></Link>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md">
            <h1 className="font-display text-3xl font-semibold tracking-tight">
              {tab === "login" ? "Welcome back" : "Create your account"}
            </h1>
            <p className="mt-2 text-muted-foreground text-sm">
              {tab === "login" ? "Sign in to continue your insurance conversations." : "Start exploring insurance through natural conversation."}
            </p>

            <Button
              data-testid="auth-google-btn"
              onClick={startGoogle}
              variant="outline"
              className="w-full h-11 rounded-xl mt-7 font-medium"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" className="mr-2"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.2 4 9.4 8.5 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.3-5.6l-6.6-5.4C29.6 34.5 26.9 35.5 24 35.5c-5.2 0-9.6-3.3-11.2-7.9l-6.5 5C9.3 39.4 16.1 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.1 4.1-3.9 5.5l6.6 5.4C42.4 35.4 44 30 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
              Continue with Google
            </Button>

            <div className="flex items-center gap-4 my-6">
              <Separator className="flex-1" />
              <span className="text-[11px] uppercase tracking-widest text-muted-foreground">or</span>
              <Separator className="flex-1" />
            </div>

            <Tabs value={tab} onValueChange={setTab} className="w-full">
              <TabsList className="w-full grid grid-cols-2 rounded-xl">
                <TabsTrigger data-testid="auth-tab-login" value="login" className="rounded-lg">Sign in</TabsTrigger>
                <TabsTrigger data-testid="auth-tab-register" value="register" className="rounded-lg">Create account</TabsTrigger>
              </TabsList>

              <TabsContent value="login" className="mt-6">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</Label>
                    <div className="relative mt-1.5">
                      <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input data-testid="login-email-input" id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="pl-10 h-11 rounded-xl" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Password</Label>
                    <div className="relative mt-1.5">
                      <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input data-testid="login-password-input" id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pl-10 h-11 rounded-xl" />
                    </div>
                    <div className="mt-2 text-right">
                      <Link data-testid="forgot-password-link" to="/forgot-password" className="text-xs text-secondary hover:underline">Forgot password?</Link>
                    </div>
                  </div>
                  <Button data-testid="login-submit-btn" type="submit" disabled={loading} className="w-full h-11 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground shadow-md">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4 ml-1.5" /></>}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register" className="mt-6">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Name</Label>
                    <div className="relative mt-1.5">
                      <UserIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input data-testid="register-name-input" id="name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Aarav Sharma" className="pl-10 h-11 rounded-xl" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="r-email" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</Label>
                    <div className="relative mt-1.5">
                      <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input data-testid="register-email-input" id="r-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="pl-10 h-11 rounded-xl" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="r-password" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Password</Label>
                    <div className="relative mt-1.5">
                      <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input data-testid="register-password-input" id="r-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="pl-10 h-11 rounded-xl" />
                    </div>
                  </div>
                  <Button data-testid="register-submit-btn" type="submit" disabled={loading} className="w-full h-11 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground shadow-md">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create account <ArrowRight className="w-4 h-4 ml-1.5" /></>}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            <p className="mt-8 text-[11px] text-muted-foreground leading-relaxed text-center">
              By continuing you agree to InsureGenie&apos;s terms and acknowledge that responses are educational only —
              not a substitute for advice from a licensed insurance, tax, or financial professional.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
